import { useEffect } from 'react';

export interface HotkeyOptions {
  /** Requires Ctrl on Windows/Linux or Cmd on macOS. */
  mod?: boolean;
  shift?: boolean;
  /** Fire even while a text field has focus. Off by default. */
  allowInInput?: boolean;
  enabled?: boolean;
}

const isEditable = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
  );
};

/**
 * Bind a keyboard shortcut for the lifetime of a component.
 *
 * Typing "b" into a search box must never collapse the sidebar, so handlers are
 * suppressed inside editable elements unless the caller opts out — which is what
 * makes shortcuts safe to add without auditing every input on the page.
 */
export const useHotkey = (
  key: string,
  handler: (event: KeyboardEvent) => void,
  options: HotkeyOptions = {},
): void => {
  const { mod = false, shift = false, allowInInput = false, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;

    const listener = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== key.toLowerCase()) return;
      if (mod !== (event.metaKey || event.ctrlKey)) return;
      if (shift !== event.shiftKey) return;
      if (!allowInInput && isEditable(event.target)) return;

      event.preventDefault();
      handler(event);
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [key, handler, mod, shift, allowInInput, enabled]);
};
